import { z } from 'zod';

/**
 * SIL-825 — component manifest versioning and change history
 * (db/schema/811_asset_manifest_version.sql).
 *
 * A manifest is a CLAIM WITH A DATE, not a permanent property. These
 * schemas render every manifest fact as a dated, tier-qualified statement —
 * never a verdict, a warning, or a risk score. Nothing here may ever gain a
 * severity/boolean-suspicious field; if a later ticket wants one, it is a
 * derived render-time annotation, not part of this DTO.
 */

export const manifestDeviceKindSchema = z.enum(['chassis', 'gpu', 'accelerator']);
export type ManifestDeviceKind = z.infer<typeof manifestDeviceKindSchema>;

export const manifestComponentClientSchema = z.object({
  deviceKind: manifestDeviceKindSchema,
  deviceIndex: z.number().int().nullable(),
  manufacturerId: z.string().nullable(),
  manufacturerName: z.string().nullable(),
  normalizedSerial: z.string().nullable(),
  modelName: z.string().nullable(),
});
export type ManifestComponentClient = z.infer<typeof manifestComponentClientSchema>;

/** One manifest version, fully rendered — the current manifest or any past one. */
export const manifestVersionClientSchema = z.object({
  id: z.string().uuid(),
  versionSeq: z.number().int().positive(),
  recordedAt: z.string(),
  verificationTier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  components: z.array(manifestComponentClientSchema),
});
export type ManifestVersionClient = z.infer<typeof manifestVersionClientSchema>;

/**
 * One dated, tier-qualified FACT between two adjacent manifest versions —
 * "GPU in slot 3 changed on this date, confirmed out-of-band." Never a
 * verdict: `changed` is the only judgment made (real swap vs. a slot that
 * simply reshuffled), computed by fn_asset_manifest_version_diff at read
 * time. The buyer judges what a change means; this only states that it
 * happened, when, and at what verification tier.
 */
export const manifestChangeClientSchema = z.object({
  deviceKind: manifestDeviceKindSchema,
  deviceIndex: z.number().int().nullable(),
  changed: z.boolean(),
  from: z
    .object({
      manufacturerId: z.string().nullable(),
      manufacturerName: z.string().nullable(),
      normalizedSerial: z.string().nullable(),
      modelName: z.string().nullable(),
    })
    .nullable(),
  to: z
    .object({
      manufacturerId: z.string().nullable(),
      manufacturerName: z.string().nullable(),
      normalizedSerial: z.string().nullable(),
      modelName: z.string().nullable(),
    })
    .nullable(),
});
export type ManifestChangeClient = z.infer<typeof manifestChangeClientSchema>;

/** One entry in the Passport's change history — the diff between two
 * adjacent versions, dated and tier-qualified by BOTH versions involved. */
export const manifestHistoryEntryClientSchema = z.object({
  fromVersionSeq: z.number().int().positive(),
  fromRecordedAt: z.string(),
  fromVerificationTier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  toVersionSeq: z.number().int().positive(),
  toRecordedAt: z.string(),
  toVerificationTier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  changes: z.array(manifestChangeClientSchema),
});
export type ManifestHistoryEntryClient = z.infer<typeof manifestHistoryEntryClientSchema>;

/**
 * The full Passport manifest section. `current` is null when the asset has
 * no recorded manifest version at all (e.g. no verification run has yet
 * captured a component set) — rendered as "no manifest recorded", never
 * inferred or defaulted. `showManifest` mirrors the assembler's own
 * decision, taken from asset_hardware_identity.device_kind = 'chassis'
 * (775) and NEVER from manifest content (811's header) — a single-GPU
 * asset entered standalone and a chassis currently holding one GPU are
 * structurally identical at the component-set level, so the renderer must
 * not try to infer which one it is looking at.
 */
export const manifestPassportClientSchema = z.object({
  showManifest: z.boolean(),
  current: manifestVersionClientSchema.nullable(),
  history: z.array(manifestHistoryEntryClientSchema),
});
export type ManifestPassportClient = z.infer<typeof manifestPassportClientSchema>;
